'use client';

import Link from '@/components/LocaleLink';
import { useTranslation } from '@/i18n/LanguageProvider';
import {
  Smartphone, CreditCard, MapPin, Globe, Download,
  UserX, MessageCircle, HelpCircle, ChevronRight, Shield
} from 'lucide-react';

export default function SupportPage() {
  const { t } = useTranslation();

  const helpTopics = [
    { icon: Smartphone, title: t('support.topic1Title'), desc: t('support.topic1Desc'), solution: t('support.topic1Solution'), color: 'primary' },
    { icon: MapPin, title: t('support.topic2Title'), desc: t('support.topic2Desc'), solution: t('support.topic2Solution'), color: 'secondary' },
    { icon: Download, title: t('support.topic3Title'), desc: t('support.topic3Desc'), solution: t('support.topic3Solution'), color: 'accent' },
    { icon: CreditCard, title: t('support.topic4Title'), desc: t('support.topic4Desc'), solution: t('support.topic4Solution'), color: 'primary' },
    { icon: Globe, title: t('support.topic5Title'), desc: t('support.topic5Desc'), solution: t('support.topic5Solution'), color: 'secondary' },
    { icon: UserX, title: t('support.topic6Title'), desc: t('support.topic6Desc'), solution: t('support.topic6Solution'), color: 'accent' },
  ];

  return (
    <div>
      <section className="bg-gradient-to-br from-primary-600 to-secondary-600 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <HelpCircle className="w-16 h-16 mx-auto mb-4 opacity-80" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{t('support.title')}</h1>
          <p className="text-xl text-white/90">{t('support.subtitle')}</p>
        </div>
      </section>

      <section className="py-8 bg-gray-50 dark:bg-gray-900 border-b dark:border-gray-700">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            <Link href="/faq" className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm text-gray-700 dark:text-gray-300 hover:text-primary hover:shadow-md transition-all"><MessageCircle className="w-4 h-4" /> FAQ</Link>
            <Link href="/contact" className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm text-gray-700 dark:text-gray-300 hover:text-primary hover:shadow-md transition-all"><MessageCircle className="w-4 h-4" /> {t('footer.contact')}</Link>
            <Link href="/delete-account" className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm text-gray-700 dark:text-gray-300 hover:text-primary hover:shadow-md transition-all"><UserX className="w-4 h-4" /> {t('footer.deleteAccount')}</Link>
            <Link href="/privacy" className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm text-gray-700 dark:text-gray-300 hover:text-primary hover:shadow-md transition-all"><Shield className="w-4 h-4" /> {t('footer.privacy')}</Link>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-8 text-center">{t('support.commonProblems')}</h2>
          <div className="space-y-6">
            {helpTopics.map((topic, index) => {
              const Icon = topic.icon;
              const bgColor = topic.color === 'primary' ? 'bg-primary-100' : topic.color === 'secondary' ? 'bg-secondary-100' : 'bg-accent-100';
              const iconColor = topic.color === 'primary' ? 'text-primary' : topic.color === 'secondary' ? 'text-secondary' : 'text-accent';
              return (
                <div key={index} className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6 md:p-8">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 ${bgColor} rounded-xl flex items-center justify-center flex-shrink-0`}><Icon className={`w-6 h-6 ${iconColor}`} /></div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">{topic.title}</h3>
                      <p className="text-gray-500 dark:text-gray-400 text-sm mb-3">{topic.desc}</p>
                      <p className="text-gray-600 dark:text-gray-300">{topic.solution}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">{t('faq.stillQuestions')}</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-lg mx-auto">{t('faq.contactUs')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="mailto:soporte@iterogo.com" className="btn-primary inline-flex items-center justify-center">{t('contact.technicalSupport')} <ChevronRight className="ml-2 w-5 h-5" /></a>
            <Link href="/faq" className="btn-outline inline-flex items-center justify-center">{t('nav.faq')}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
